/**
 * Thing バージョン管理ページ
 * OTAバージョンチェック機能のテスト用
 */

import React, { useState, useEffect, useCallback } from 'react';
import { versionApi } from '../services/api';
import { Icon } from '../components/icons';
import { Card, Alert } from '../components/ui';

export default function ThingVersions() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await versionApi.list();
      setVersions(data.versions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Thing バージョン管理</h1>
          <p className="text-sm text-slate-500 mt-1">
            Androidアプリからのバージョンチェック結果を確認
          </p>
        </div>
        <button
          onClick={fetchVersions}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <Icon name="refresh" className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          更新
        </button>
      </div>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      <Card padding="p-0">
        {loading && versions.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="device" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">データがありません</p>
            <p className="text-sm text-slate-400 mt-1">
              Androidアプリからバージョンチェックを実行してください
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Thing名
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    PX4
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ametori
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Karas
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Parameter
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    チェック回数
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    更新日時
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {versions.map((v) => (
                  <tr key={v.thingName} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon name="device" className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">{v.thingName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <VersionBadge version={v.px4Version} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <VersionBadge version={v.ametoriVersion} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <VersionBadge version={v.karasVersion} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <VersionBadge version={v.parameterVersion} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                        {v.checkCount || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-500">
                      {formatDate(v.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* MQTT トピック情報 */}
      <Card title="MQTT通信情報" className="bg-slate-50">
        <div className="space-y-3 text-sm">
          <div>
            <span className="font-medium text-slate-700">バージョンチェック:</span>
            <code className="ml-2 px-2 py-1 bg-white rounded text-xs font-mono">
              version/{'{thingName}'}/check/request
            </code>
          </div>
          <div>
            <span className="font-medium text-slate-700">更新通知:</span>
            <code className="ml-2 px-2 py-1 bg-white rounded text-xs font-mono">
              version/{'{thingName}'}/update/request
            </code>
          </div>
          <p className="text-slate-500 mt-2">
            Androidアプリからバージョンチェックを実行すると、このページに表示されます。
            段階的バージョンアップロジックにより、チェック回数に応じてPX4→Ametori→Karas→Parameter→なし
            の順でアップデートが提案されます。
          </p>
        </div>
      </Card>
    </div>
  );
}

function VersionBadge({ version }) {
  if (!version) return <span className="text-slate-400">-</span>;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-primary-50 text-primary-700">
      v{version}
    </span>
  );
}
